/*
  Desayunos del Cielo · product configurator (3 steps: Personaliza · Adicionales y contactos · Revisa y paga).
  - Main product is added with every captured value as line-item properties.
  - Add-ons, zone delivery fee products and the special-slot surcharge product are
    separate cart lines (real Shopify prices) linked by the hidden `_ddc_pedido` property.
  - No sounds. Only two emotional messages: the start toast and the review banner (in Liquid).
*/
if (!customElements.get('ddc-configurator')) {
  customElements.define(
    'ddc-configurator',
    class DdcConfigurator extends HTMLElement {
      static STEP_ACTIONS = {
        1: 'Continuar a adicionales',
        2: 'Revisar pedido',
        3: 'Ir a pagar de forma segura',
      };

      static START_MESSAGE = 'Tu sorpresa ya tiene personalidad ✨';

      static TIME_ERROR =
        'No pudimos verificar la hora de la tienda. La entrega y el pago están bloqueados temporalmente; inténtalo de nuevo más tarde.';

      static COLOR_ERROR = 'Elige al menos un color para la decoración';

      static ANONYMOUS_SIGNATURE = 'Anónimo';

      // Verified in the preview checkout: this store hides "Apartamento" (address2), so the
      // indicaciones ride in address1 after " — ". Flip to true if address2 is ever enabled.
      static CHECKOUT_SHOWS_ADDRESS2 = false;

      static LAST_STEP = 3;

      static CONTACTS_STEP = 2;

      static PREPARE_ERROR = 'No pudimos preparar tu pedido, intenta de nuevo.';

      static CART_EXISTS =
        'Ya tienes un pedido en el carrito. Para evitar mezclar destinatarios, fechas o domicilios, confirma si quieres reemplazarlo.';

      static VARIANT_ERROR = 'Selecciona una opción válida y disponible del producto antes de continuar.';

      static DELIVERY_FEE_ERROR =
        'La tarifa de domicilio para esta zona no está disponible. Elige otra zona o inténtalo de nuevo más tarde.';

      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = 'true';

        this.settings = JSON.parse(this.querySelector('[data-ddc-settings]').textContent);
        this.variantIds = new Set((this.settings.variantIds || []).map(Number));
        this.variant = this.normalizedVariant(this.settings.variant);
        this.form = this.querySelector('[data-ddc-form]');
        this.config = this.querySelector('.ddc-config');
        this.mobileBuy = this.querySelector('[data-ddc-mobile]');
        this.dateInput = this.querySelector('[data-ddc-date]');
        this.zoneSelect = this.querySelector('[data-ddc-zone]');
        this.slotHelp = this.querySelector('[data-ddc-slot-help]');
        this.toast = this.querySelector('[data-ddc-toast]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.step = 1;
        this.maxStep = 1;
        this.editing = null;
        this.celebrated = false;
        this.submitting = false;

        this.dateInput.value = this.tomorrowIso();
        this.updateSlots(false);
        // Cutoff is re-evaluated every minute while the page stays open (Bogotá time, not device time).
        this.clockTimer = setInterval(() => {
          if (this.submitting) return;
          this.updateSlots(false);
          this.render();
        }, 60000);

        this.form.addEventListener('submit', (event) => event.preventDefault());
        this.form.addEventListener('change', (event) => this.onChange(event));
        this.form.addEventListener('input', (event) => this.onInput(event));
        this.addEventListener('click', (event) => this.onClick(event));


        this.config.dataset.activeStep = '1';
        this.config.classList.add('is-ready');
        if ('IntersectionObserver' in window && this.mobileBuy) {
          this.visibilityObserver = new IntersectionObserver(([entry]) => {
            this.mobileBuy.classList.toggle('is-active', entry.isIntersecting);
          }, { threshold: 0.01 });
          this.visibilityObserver.observe(this);
        } else if (this.mobileBuy) {
          this.mobileBuy.classList.add('is-active');
        }
        this.limitColors();
        this.syncAnonymous();
        this.render();
      }

      disconnectedCallback() {
        if (this.clockTimer) clearInterval(this.clockTimer);
        if (this.visibilityObserver) this.visibilityObserver.disconnect();

      }

      /* ---------- Time & dates (store time zone) ---------- */

      // Test hook: window.__DDC_NOW__ or data-ddc-now on the element (ISO string or epoch ms).
      currentInstant() {
        const override =
          (typeof window !== 'undefined' && window && window.__DDC_NOW__) ||
          (this.dataset && this.dataset.ddcNow) ||
          null;
        if (override !== null && override !== undefined && override !== '') {
          const date = new Date(typeof override === 'number' || /^\d+$/.test(String(override)) ? Number(override) : override);
          if (!Number.isNaN(date.getTime())) return date;
        }
        return new Date();
      }

      nowInStore() {
        try {
          if (!this.settings.timezone || typeof Intl === 'undefined' || !Intl.DateTimeFormat) return null;
          const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: this.settings.timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            hourCycle: 'h23',
          }).formatToParts(this.currentInstant());
          const get = (type) => parts.find((part) => part.type === type)?.value;
          const date = `${get('year')}-${get('month')}-${get('day')}`;
          const hour = Number(get('hour'));
          if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isInteger(hour) || hour < 0 || hour > 23) return null;
          return { date, hour };
        } catch (error) {
          return null;
        }
      }

      addDays(iso, days) {
        const date = new Date(`${iso}T12:00:00Z`);
        date.setUTCDate(date.getUTCDate() + days);
        return date.toISOString().slice(0, 10);
      }

      tomorrowIso() {
        const now = this.nowInStore();
        return now ? this.addDays(now.date, 1) : '';
      }

      prettyDate(iso) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return 'Fecha pendiente';
        return new Intl.DateTimeFormat('es-CO', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          timeZone: 'UTC',
        }).format(new Date(`${iso}T12:00:00Z`));
      }

      /* ---------- Slots: truthful availability + after-cutoff rule ---------- */

      slots() {
        return [...this.querySelectorAll('input[name="properties[Franja de entrega]"]')];
      }

      // Pure rule: which slots are sold out for a given Bogotá "now" and delivery date.
      slotAvailability(now, date, starts) {
        const tomorrow = this.addDays(now.date, 1);
        const afterCutoff = now.hour >= Number(this.settings.cutoffHour) && date === tomorrow;
        const lateFirst = Number(this.settings.lateFirstSlot) || 10;
        return { afterCutoff, soldOut: starts.map((start) => afterCutoff && Number(start) < lateFirst) };
      }

      setSlotSoldOut(slot, soldOut) {
        slot.disabled = soldOut;
        if (soldOut) {
          slot.setAttribute('aria-disabled', 'true');
          slot.checked = false;
        } else {
          slot.removeAttribute('aria-disabled');
        }
        const label = slot.closest('[data-ddc-slot-label]');
        if (label) {
          label.classList.toggle('is-soldout', soldOut);
          if (soldOut) label.setAttribute('aria-label', `${slot.dataset.label} · No disponible por hora límite`);
          else label.removeAttribute('aria-label');
        }
      }

      setSlotNote(text) {
        if (!this.slotHelp) return;
        this.slotHelp.textContent = text || '';
        this.slotHelp.hidden = !text;
      }

      updateSlots(announce) {
        const now = this.nowInStore();
        if (!now) {
          this.storeTimeAvailable = false;
          this.dateInput.removeAttribute('min');
          this.slots().forEach((slot) => {
            slot.disabled = true;
          });
          this.setSlotNote(DdcConfigurator.TIME_ERROR);
          return false;
        }

        this.storeTimeAvailable = true;
        const tomorrow = this.addDays(now.date, 1);
        this.dateInput.min = tomorrow;
        // Today (Bogotá) is never selectable here; same-day orders go through WhatsApp.
        if (!this.dateInput.value || this.dateInput.value < tomorrow) this.dateInput.value = tomorrow;

        const slots = this.slots();
        const rule = this.slotAvailability(now, this.dateInput.value, slots.map((slot) => slot.dataset.start));
        slots.forEach((slot, i) => this.setSlotSoldOut(slot, rule.soldOut[i]));

        let selected = slots.find((slot) => slot.checked && !slot.disabled);
        if (!selected) {
          selected =
            slots.find((slot) => !slot.disabled && !slot.hasAttribute('data-special')) ||
            slots.find((slot) => !slot.disabled);
          if (selected) selected.checked = true;
        }

        if (rule.afterCutoff) {
          const open = slots.filter((slot) => !slot.disabled).map((slot) => slot.dataset.label);
          this.setSlotNote(`Para mañana solo queda disponible ${open.join(', ') || '10:00 a. m.–12:00 p. m.'}`);
        } else if (announce && selected && selected.hasAttribute('data-special')) {
          this.setSlotNote(`Entrega especial: el total incluye ${this.money(this.settings.special.price)}.`);
        } else {
          this.setSlotNote('');
        }
        return true;
      }

      /* ---------- Reading state from the form (single source of truth) ---------- */

      selectedSlot() {
        return this.slots().find((slot) => slot.checked && !slot.disabled) || null;
      }

      selectedZone() {
        if (!this.zoneSelect) return null;
        const option = this.zoneSelect.selectedOptions[0];
        if (!option || !option.value || !option.dataset.name) return null;
        return {
          name: option.dataset.name,
          city: option.dataset.city || '',
          province: option.dataset.province || '',
          variantId: option.dataset.variant ? Number(option.dataset.variant) : null,
          price: option.dataset.price ? Number(option.dataset.price) : null,
          disabled: option.disabled,
        };
      }

      deliveryFeeReady(zone = this.selectedZone()) {
        return Boolean(
          zone &&
            zone.name &&
            Number.isInteger(zone.variantId) &&
            zone.variantId > 0 &&
            Number.isFinite(zone.price) &&
            zone.price >= 0 &&
            !zone.disabled
        );
      }

      selectedColors() {
        const selected = [];
        const seenColors = new Set();
        [...this.querySelectorAll('[data-ddc-color]:checked')].forEach((input) => {
          const value = String(input.value || '').trim();
          const key = value.toLocaleLowerCase('es-CO');
          if (!value || seenColors.has(key)) return;
          seenColors.add(key);
          selected.push(value);
        });
        return selected;
      }

      selectedAddons() {
        return [...this.querySelectorAll('[data-ddc-addon]:checked:not(:disabled)')].map((input) => ({
          variantId: Number(input.dataset.variant),
          price: Number(input.dataset.price),
          title: input.dataset.title,
        }));
      }

      isSpecial() {
        const slot = this.selectedSlot();
        return Boolean(slot && slot.hasAttribute('data-special') && this.settings.special);
      }

      totals() {
        const zone = this.selectedZone();
        const addons = this.selectedAddons();
        const product = this.variant ? Number(this.variant.price) || 0 : 0;
        const zoneFee = zone && zone.price !== null ? zone.price : 0;
        const special = this.isSpecial() ? this.settings.special.price : 0;
        const extras = addons.reduce((sum, addon) => sum + addon.price, 0);
        return {
          product,
          zone,
          zoneKnown: !zone || zone.price !== null,
          special,
          addons,
          extras,
          total: product + zoneFee + special + extras,
        };
      }

      money(cents) {
        const amount = (Number(cents) || 0) / 100;
        if (this.settings.currency === 'COP') {
          return `$${Math.round(amount).toLocaleString('es-CO')}`;
        }
        try {
          return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: this.settings.currency,
            maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
          }).format(amount);
        } catch (error) {
          return amount.toFixed(2);
        }
      }

      value(selector) {
        const field = this.querySelector(selector);
        return field ? field.value.trim() : '';
      }

      /* ---------- Events ---------- */

      normalizedVariant(variant) {
        if (!variant) return null;
        const id = Number(variant.id);
        const price = Number(variant.price);
        if (!Number.isInteger(id) || !this.variantIds.has(id) || !Number.isFinite(price)) return null;
        return { id, price, available: variant.available === true, title: variant.title || '' };
      }

      variantEventProductId(event) {
        const data = event && event.data;
        const picker = data && (data.target?.closest?.('variant-selects') || data.event?.target?.closest?.('variant-selects'));
        const productInfo = data && data.html?.querySelector?.('product-info[data-product-id]');
        const id = Number(picker?.dataset.productId || productInfo?.dataset.productId);
        return Number.isInteger(id) ? id : null;
      }

      eventIsForProduct(event) {
        const productId = this.variantEventProductId(event);
        if (productId !== null) return productId === Number(this.settings.productId);
        const variantId = Number(event?.data?.variant?.id);
        return Number.isInteger(variantId) && this.variantIds.has(variantId);
      }

      setVariant(variant) {
        this.variant = this.normalizedVariant(variant);
        this.querySelector('[data-ddc-variant-input]').value = this.variant ? String(this.variant.id) : '';
        const localPicker = this.querySelector('[data-ddc-variant-select]');
        if (localPicker && this.variant) localPicker.value = String(this.variant.id);
      }

      variantReady() {
        return Boolean(this.variant && this.variant.available);
      }

      validateVariant() {
        if (this.variantReady()) return true;
        this.showCartError(DdcConfigurator.VARIANT_ERROR);
        return false;
      }

      onInput(event) {
        this.clearMissing();
        if (event.target.matches('[data-ddc-contact]')) {
          event.target.removeAttribute('aria-invalid');
        }
        if (event.target.matches('[data-ddc-message]')) this.renderMessageCount();
        this.renderReview();
      }

      onChange(event) {
        const target = event.target;
        this.clearMissing();
        if (target === this.dateInput) {
          target.removeAttribute('aria-invalid');
          this.updateSlots(false);
        }
        if (target === this.zoneSelect) target.removeAttribute('aria-invalid');
        if (target.matches('[data-ddc-variant-select]')) {
          const variant = (this.settings.variants || []).find((item) => Number(item.id) === Number(target.value));
          this.setVariant(variant || null);
          this.showCartError(this.variantReady() ? '' : DdcConfigurator.VARIANT_ERROR);
        }
        if (target.name === 'properties[Franja de entrega]') this.updateSlots(true);
        if (target.matches('[data-ddc-color]')) {
          this.limitColors();
          if (this.selectedColors().length) this.setColorError('');
        }
        if (target.matches('[data-ddc-anonymous]')) this.syncAnonymous();
        this.render(true);
      }

      onClick(event) {
        if (event.target.closest('[data-ddc-replace-cart]')) {
          this.checkout({ replaceExisting: true });
          return;
        }
        const goStep = event.target.closest('[data-go-step]');
        if (goStep) {
          const target = Number(goStep.dataset.goStep);
          // Progress buttons only navigate back/within reached steps; "skip" links move forward.
          if (goStep.closest('[data-ddc-progress]') && target > this.maxStep) return;
          this.goTo(target);
          return;
        }
        if (event.target.closest('[data-ddc-next]')) {
          this.next();
          return;
        }
        if (event.target.closest('[data-ddc-back]')) {
          this.goTo(this.step - 1);
          return;
        }
        const edit = event.target.closest('[data-edit]');
        if (edit) {
          this.startEdit(edit.dataset.edit);
          return;
        }
        if (event.target.closest('[data-edit-save]')) {
          this.saveEdit();
          return;
        }
        if (event.target.closest('[data-edit-cancel]')) {
          this.cancelEdit();
        }
      }

      onVariantChange(event) {
        if (!this.eventIsForProduct(event)) return;
        const variant = event && event.data && event.data.variant;
        this.setVariant(variant);
        if (this.variantReady()) this.showCartError('');
        else this.showCartError(DdcConfigurator.VARIANT_ERROR);
        this.render(true);
      }

      onOptionSelectionChange(event) {
        if (!this.eventIsForProduct(event)) return;
        this.setVariant(null);
        this.showCartError(DdcConfigurator.VARIANT_ERROR);
        this.render(true);
      }

      onVariantInputChange(event) {
        const input = event.target;
        if (!input?.matches?.('input[name="id"]') || this.contains(input)) return;
        const productInfo = input.closest('product-info[data-product-id]');
        if (!productInfo || Number(productInfo.dataset.productId) !== Number(this.settings.productId)) return;
        if (!this.variant || String(this.variant.id) !== input.value) {
          this.setVariant(null);
          this.showCartError(DdcConfigurator.VARIANT_ERROR);
          this.render(true);
        }
      }

      limitColors() {
        const max = this.settings.maxColors;
        const inputs = [...this.querySelectorAll('[data-ddc-color]')];
        const seenColors = new Set();
        const duplicateInputs = new Set();
        inputs.forEach((input) => {
          const key = String(input.value || '').trim().toLocaleLowerCase('es-CO');
          if (!key || seenColors.has(key)) {
            input.checked = false;
            duplicateInputs.add(input);
            return;
          }
          seenColors.add(key);
        });
        const initiallyChecked = inputs.filter((input) => input.checked);
        initiallyChecked.slice(max).forEach((input) => {
          input.checked = false;
        });
        const count = inputs.filter((input) => input.checked).length;
        inputs.forEach((input) => {
          input.disabled = duplicateInputs.has(input) || (count >= max && !input.checked);
        });
        const counter = this.querySelector('[data-ddc-color-count]');
        if (counter) counter.textContent = `${count} de ${max}`;
      }

      /* ---------- Anonymous card ---------- */

      isAnonymous() {
        const box = this.querySelector('[data-ddc-anonymous]');
        return Boolean(box && box.checked);
      }

      syncAnonymous() {
        const field = this.querySelector('[data-ddc-signature]');
        if (!field) return;
        if (this.isAnonymous()) {
          if (!field.disabled) {
            this.savedSignature = field.value;
            field.value = '';
          }
          field.disabled = true;
          field.placeholder = DdcConfigurator.ANONYMOUS_SIGNATURE;
          field.removeAttribute('aria-invalid');
        } else {
          if (field.disabled) {
            field.value = this.savedSignature || '';
          }
          field.disabled = false;
          field.placeholder = 'Tu nombre o apodo';
        }
        field.closest('.ddc-field')?.classList.toggle('is-anonymous', this.isAnonymous());
      }

      signatureText() {
        return this.isAnonymous() ? DdcConfigurator.ANONYMOUS_SIGNATURE : this.value('[data-ddc-signature]');
      }

      /* ---------- Validation ---------- */

      setError(group, message, motivo = '') {
        const error = group.querySelector('[data-ddc-error]');
        if (!error) return;
        // 2026-10-29 · El motivo (campo exacto que falta) viaja a GA4 en ddc_config_error (ddc-events.js).
        error.dataset.motivo = message ? motivo || '' : '';
        error.textContent = message || '';
        error.hidden = !message;
      }

      validateDelivery() {
        const group = this.querySelector('[data-group="delivery"]');
        const timeAvailable = this.updateSlots(false);
        let first = null;
        let message = '';

        let motivo = '';

        if (!timeAvailable) {
          this.dateInput.setAttribute('aria-invalid', 'true');
          this.setError(group, DdcConfigurator.TIME_ERROR, 'hora_tienda');
          this.dateInput.dataset.ddcMotivo = 'hora_tienda';
          return this.dateInput;
        }

        const zone = this.selectedZone();
        if (this.zoneSelect && !this.deliveryFeeReady(zone)) {
          this.zoneSelect.setAttribute('aria-invalid', 'true');
          first = this.zoneSelect;
          motivo = zone?.name ? 'tarifa_domicilio' : 'zona';
          message = zone?.name ? DdcConfigurator.DELIVERY_FEE_ERROR : 'Selecciona la zona o municipio de entrega.';
        } else if (this.zoneSelect) {
          this.zoneSelect.removeAttribute('aria-invalid');
        }

        const date = this.dateInput.value;
        const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= this.dateInput.min;
        if (dateValid) this.dateInput.removeAttribute('aria-invalid');
        else this.dateInput.setAttribute('aria-invalid', 'true');
        if (!dateValid && !first) {
          first = this.dateInput;
          motivo = 'fecha';
          message = 'Elige una fecha desde mañana. Para entregas de hoy escríbenos por WhatsApp.';
        }

        if (!this.selectedSlot() && !first) {
          first = this.slots().find((slot) => !slot.disabled) || this.dateInput;
          motivo = 'franja';
          message = 'Elige una franja de entrega disponible.';
        }

        this.setError(group, message, motivo);
        if (first) first.dataset.ddcMotivo = motivo;
        return first;
      }

      setColorError(message) {
        const error = this.querySelector('[data-ddc-color-error]');
        if (!error) return;
        error.dataset.motivo = message ? 'colores' : '';
        error.textContent = message || '';
        error.hidden = !message;
        const fieldset = this.querySelector('[data-ddc-colors]');
        if (fieldset) fieldset.classList.toggle('is-invalid', Boolean(message));
      }

      // Colors are mandatory (1..maxColors) when the store offers colors.
      validateColors() {
        if (!this.querySelector('[data-ddc-color]')) return null;
        const count = this.selectedColors().length;
        if (count >= 1 && count <= Number(this.settings.maxColors || 2)) {
          this.setColorError('');
          return null;
        }
        this.setColorError(DdcConfigurator.COLOR_ERROR);
        const colorError = this.querySelector('[data-ddc-color-error]');
        if (colorError) colorError.dataset.ddcMotivo = 'colores';
        return colorError;
      }

      // 2026-10-29 · Motivo y texto por campo. Antes el aviso era genérico ("Completa los datos marcados")
      // y un teléfono copiado de WhatsApp/Contactos en iPhone (con marcas invisibles U+202A/U+202C o puntos)
      // se rechazaba sin explicar por qué: una misma persona repetía el error 9 veces.
      static contactInfo(input) {
        const kind = input.dataset.ddcContact;
        const sender = input.hasAttribute('data-ddc-sender-name') || input.hasAttribute('data-ddc-sender-phone') || input.hasAttribute('data-ddc-sender-email');
        if (kind === 'phone') return sender
          ? { motivo: 'whatsapp_envia', empty: 'Falta tu WhatsApp', bad: 'Revisa tu WhatsApp: escribe 10 números, por ejemplo 300 123 4567' }
          : { motivo: 'whatsapp_recibe', empty: 'Falta el WhatsApp de quien recibe', bad: 'Revisa el WhatsApp de quien recibe: 10 números, por ejemplo 300 123 4567' };
        if (kind === 'email') return { motivo: 'correo', empty: 'Falta tu correo', bad: 'Revisa tu correo, por ejemplo nombre@gmail.com' };
        if (kind === 'address') return { motivo: 'direccion', empty: 'Falta la dirección de entrega', bad: 'Escribe la dirección completa: calle, número y barrio' };
        return sender
          ? { motivo: 'nombre_envia', empty: 'Falta tu nombre', bad: 'Escribe tu nombre' }
          : { motivo: 'nombre_recibe', empty: 'Falta el nombre de quien recibe', bad: 'Escribe el nombre de quien recibe' };
      }

      static cleanPhone(value) {
        // Quita marcas invisibles de dirección de texto y separadores comunes (puntos, guiones, paréntesis, espacios).
        return String(value || '').replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g, '').trim();
      }

      validateContacts() {
        const group = this.querySelector('[data-group="contacts"]');
        let first = null;
        let firstInfo = null;
        let firstEmpty = false;
        const motivos = [];
        group.querySelectorAll('[data-ddc-contact]').forEach((input) => {
          const kind = input.dataset.ddcContact;
          if (kind === 'phone') {
            const cleaned = DdcConfigurator.cleanPhone(input.value);
            if (cleaned !== input.value) input.value = cleaned;
          }
          const value = input.value.trim();
          let valid = value.length >= 2;
          if (kind === 'phone') {
            const digits = value.replace(/\D/g, '');
            valid = /^[+()\-.\s\d]+$/.test(value) && digits.length >= 10 && digits.length <= 15;
          }
          if (kind === 'email') valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
          if (kind === 'address') valid = value.length >= 6;
          if (valid) {
            input.removeAttribute('aria-invalid');
          } else {
            input.setAttribute('aria-invalid', 'true');
            const info = DdcConfigurator.contactInfo(input);
            motivos.push(info.motivo);
            if (!first) {
              first = input;
              firstInfo = info;
              firstEmpty = value.length === 0;
              input.dataset.ddcMotivo = info.motivo;
            }
          }
        });
        let message = '';
        if (first) {
          message = firstEmpty ? `${firstInfo.empty}.` : `${firstInfo.bad}.`;
          if (motivos.length > 1) message += ` Faltan ${motivos.length} datos marcados en rojo.`;
        }
        this.setError(group, message, motivos.join(','));
        return first;
      }

      /* 2026-10-29 · Aviso junto al botón fijo de abajo (y del resumen en escritorio) cuando falta algo. */
      static MISSING_LABELS = {
        zona: 'Falta elegir la zona de entrega',
        tarifa_domicilio: 'Elige otra zona de entrega',
        fecha: 'Falta elegir la fecha de entrega',
        franja: 'Falta elegir la franja de entrega',
        hora_tienda: 'No pudimos verificar la hora; intenta de nuevo',
        colores: 'Falta elegir al menos un color',
        nombre_envia: 'Falta tu nombre',
        whatsapp_envia: 'Revisa tu WhatsApp',
        correo: 'Revisa tu correo',
        nombre_recibe: 'Falta el nombre de quien recibe',
        whatsapp_recibe: 'Revisa el WhatsApp de quien recibe',
        direccion: 'Falta la dirección de entrega',
      };

      announceMissing(field) {
        const motivo = (field && field.dataset && field.dataset.ddcMotivo) || '';
        let text = DdcConfigurator.MISSING_LABELS[motivo] || 'Falta completar un dato';
        if (motivo === 'whatsapp_envia' || motivo === 'whatsapp_recibe' || motivo === 'correo') {
          if (field.value && !field.value.trim()) text = text.replace('Revisa', 'Falta');
          if (!field.value) text = text.replace('Revisa', 'Falta');
        }
        this.querySelectorAll('[data-ddc-missing]').forEach((el) => {
          el.textContent = text;
          el.hidden = false;
        });
        // Mantiene visible la barra fija aunque el campo reciba el foco (ddc-mobile-fixes la oculta al escribir).
        document.body.classList.add('ddc-missing-on');
        clearTimeout(this.missingTimer);
        this.missingTimer = setTimeout(() => this.clearMissing(), 7000);
        const box = field?.closest?.('.ddc-field, fieldset, [data-ddc-colors]') || field;
        if (box && box.classList) {
          box.classList.remove('ddc-attn');
          void box.offsetWidth;
          box.classList.add('ddc-attn');
          setTimeout(() => box.classList.remove('ddc-attn'), 2400);
        }
      }

      clearMissing() {
        clearTimeout(this.missingTimer);
        document.body.classList.remove('ddc-missing-on');
        this.querySelectorAll('[data-ddc-missing]').forEach((el) => {
          el.hidden = true;
          el.textContent = '';
        });
      }

      validateGroup(name) {
        if (name === 'delivery') return this.validateDelivery();
        if (name === 'contacts') return this.validateContacts();
        if (name === 'personalization') return this.validateColors();
        return null;
      }

      focusInvalid(field) {
        field.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
        field.focus({ preventScroll: true });
      }

      /* ---------- Navigation ---------- */

      next() {
        if (this.step < DdcConfigurator.LAST_STEP) {
          this.goTo(this.step + 1);
        } else {
          this.checkout();
        }
      }

      goTo(target) {
        if (this.editing || this.submitting) return;
        target = Math.min(Math.max(target, 1), DdcConfigurator.LAST_STEP);

        if (target > this.step) {
          if (!this.validateVariant()) return;
          if (this.step <= 1) {
            const invalid = this.validateDelivery() || this.validateColors();
            if (invalid) {
              if (this.step !== 1) this.showStep(1);
              this.focusInvalid(invalid);
              this.announceMissing(invalid);
              return;
            }
          }
          // Contacts live in step 2; they are validated when leaving it.
          if (target > DdcConfigurator.CONTACTS_STEP) {
            const invalid = this.validateContacts();
            if (invalid) {
              if (this.step !== DdcConfigurator.CONTACTS_STEP) this.showStep(DdcConfigurator.CONTACTS_STEP);
              this.focusInvalid(invalid);
              this.announceMissing(invalid);
              return;
            }
          }
          if (this.step === 1 && !this.celebrated) {
            this.celebrated = true;
            this.showToast(DdcConfigurator.START_MESSAGE);
          }
        }

        this.showStep(target);
      }

      showStep(target) {
        this.step = target;
        this.maxStep = Math.max(this.maxStep, target);
        this.config.dataset.activeStep = String(target);
        this.showCartError('');
        this.querySelectorAll('[data-step]').forEach((section) => {
          section.hidden = Number(section.dataset.step) !== target;
        });
        this.render();
        this.config.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
        const current = this.querySelector(`[data-step="${target}"]`);
        current.focus({ preventScroll: true });
      }

      showToast(message) {
        this.toast.textContent = message;
        this.toast.classList.add('is-visible');
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 1800);
      }

      /* ---------- Inline editing on the review step ---------- */

      snapshot(group) {
        return [...group.querySelectorAll('input, select, textarea')].map((field) => ({
          field,
          value: field.value,
          checked: field.checked,
          disabled: field.disabled,
        }));
      }

      startEdit(name) {
        if (this.submitting) return;
        if (this.editing) this.cancelEdit();
        const group = this.querySelector(`[data-group="${name}"]`);
        const card = this.querySelector(`[data-review-card="${name}"]`);
        if (!group || !card) return;

        this.editing = { name, group, card, snapshot: this.snapshot(group) };
        card.querySelector('[data-editor-slot]').appendChild(group);
        card.querySelector('[data-editor]').hidden = false;
        card.querySelector('[data-edit]').hidden = true;
        card.classList.add('is-editing');
        this.render();

        const first = group.querySelector('input:not([type="hidden"]):not(:disabled), select, textarea');
        if (first) first.focus();
      }

      finishEdit() {
        const { name, group, card } = this.editing;
        this.querySelector(`[data-home="${name}"]`).appendChild(group);
        card.querySelector('[data-editor]').hidden = true;
        const editButton = card.querySelector('[data-edit]');
        editButton.hidden = false;
        card.classList.remove('is-editing');
        this.editing = null;
        this.render();
        editButton.focus();
        return card;
      }

      saveEdit() {
        if (!this.editing) return;
        const invalid = this.validateGroup(this.editing.name);
        if (invalid) {
          this.focusInvalid(invalid);
          return;
        }
        const card = this.finishEdit();
        const saved = card.querySelector('[data-ddc-saved]');
        saved.hidden = false;
        setTimeout(() => {
          saved.hidden = true;
        }, 1000);
      }

      cancelEdit() {
        if (!this.editing) return;
        this.editing.snapshot.forEach(({ field, value, checked, disabled }) => {
          field.value = value;
          field.checked = checked;
          field.disabled = disabled;
          field.removeAttribute('aria-invalid');
        });
        this.setError(this.editing.group, '');
        if (this.editing.name === 'delivery') this.updateSlots(false);
        if (this.editing.name === 'personalization') {
          this.limitColors();
          this.setColorError('');
          const field = this.querySelector('[data-ddc-signature]');
          if (field) field.closest('.ddc-field')?.classList.toggle('is-anonymous', this.isAnonymous());
        }
        this.finishEdit();
      }

      /* ---------- Rendering ---------- */

      renderMessageCount() {
        const message = this.querySelector('[data-ddc-message]');
        const counter = this.querySelector('[data-ddc-message-count]');
        if (message && counter) counter.textContent = `${message.value.length}/500`;
      }

      lineRow(label, amount) {
        const row = document.createElement('div');
        row.className = 'ddc-line';
        const name = document.createElement('span');
        name.textContent = label;
        const price = document.createElement('span');
        price.textContent = amount;
        row.append(name, price);
        return row;
      }

      render(bump = false) {
        const t = this.totals();

        // Summary lines
        this.querySelector('[data-ddc-product-price]').textContent = this.money(t.product);
        const headPrice = this.querySelector('[data-ddc-head-price]');
        if (headPrice) headPrice.textContent = this.money(t.product);
        const variantTitle = this.querySelector('[data-ddc-variant-title]');
        if (variantTitle && !this.settings.hasOnlyDefaultVariant) variantTitle.textContent = this.variant?.title || '';

        const zoneLine = this.querySelector('[data-ddc-zone-line]');
        if (zoneLine) {
          const zoneLabel = this.querySelector('[data-ddc-zone-label]');
          const zonePrice = this.querySelector('[data-ddc-zone-price]');
          if (t.zoneKnown && t.zone) {
            zoneLabel.textContent = `Domicilio · ${t.zone.name}`;
            zonePrice.textContent = this.money(t.zone.price);
          } else {
            zoneLabel.textContent = 'Domicilio';
            zonePrice.textContent = 'Selecciona una zona para calcularlo';
          }
          zoneLine.classList.toggle('ddc-line--muted', !t.zoneKnown);
        }

        const specialLine = this.querySelector('[data-ddc-special-line]');
        specialLine.hidden = !t.special;
        if (t.special) this.querySelector('[data-ddc-special-price]').textContent = `+${this.money(t.special)}`;

        this.querySelector('[data-ddc-addon-lines]').replaceChildren(
          ...t.addons.map((addon) => this.lineRow(addon.title, `+${this.money(addon.price)}`))
        );

        const totalText = this.money(t.total);
        this.querySelectorAll('[data-ddc-total]').forEach((el) => {
          const changed = el.textContent !== totalText;
          el.textContent = totalText;
          if (bump && changed && !this.reducedMotion.matches) {
            el.classList.remove('ddc-total-bump');
            void el.offsetWidth;
            el.classList.add('ddc-total-bump');
          }
        });
        this.querySelector('[data-ddc-total-label]').textContent = t.zoneKnown ? 'Total' : 'Subtotal';
        let mobileLabel = t.zoneKnown ? 'Total' : 'Subtotal · falta elegir zona';
        if (t.special) mobileLabel += ' · incluye horario especial';
        this.querySelector('[data-ddc-mobile-label]').textContent = mobileLabel;

        // Hidden properties kept in sync with visible choices
        const colors = this.selectedColors();
        this.querySelector('[data-ddc-colors-value]').value = colors.join(' y ');
        this.querySelector('[data-ddc-addons-value]').value = t.addons.map((addon) => addon.title).join(', ');

        // Progress
        const progress = this.querySelector('[data-ddc-progress]');
        progress.style.setProperty('--ddc-fill', String((this.step - 1) / (DdcConfigurator.LAST_STEP - 1)));
        progress.querySelectorAll('[data-progress-step]').forEach((item) => {
          const n = Number(item.dataset.progressStep);
          const button = item.querySelector('button');
          item.classList.toggle('is-active', n === this.step);
          item.classList.toggle('is-done', n < this.step || (n <= this.maxStep && n !== this.step));
          item.querySelector('.ddc-progress__num').textContent = n < this.step ? '✓' : String(n);
          if (n === this.step) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
          button.disabled = n > this.maxStep || Boolean(this.editing);
        });

        // Primary actions
        const blocked = Boolean(this.editing) || this.submitting;
        this.querySelectorAll('[data-ddc-next]').forEach((button) => {
          const mobile = Boolean(button.closest('[data-ddc-mobile]'));
          let label = DdcConfigurator.STEP_ACTIONS[this.step];
          if (this.step === DdcConfigurator.LAST_STEP && mobile) label = 'Ir a pagar';
          if (this.editing) label = 'Guarda para continuar';
          if (this.submitting) label = 'Preparando tu pedido…';
          button.textContent = label;
          button.disabled = blocked || !this.variantReady() || this.storeTimeAvailable === false;
        });
        const back = this.querySelector('[data-ddc-back]');
        back.hidden = this.step === 1;
        back.disabled = blocked;
        back.textContent = this.step === DdcConfigurator.LAST_STEP ? 'Volver a contactos' : 'Volver';

        this.querySelectorAll('.ddc-pay--aside').forEach((box) => {
          box.hidden = this.step !== DdcConfigurator.LAST_STEP;
        });

        this.renderMessageCount();
        this.renderReview();
      }

      // Review rows: muted label + bold value, optional icon (date/time/zone/person).
      reviewRows(container, rows) {
        const icons = {
          pin: '<path d="M12 21s-6-5.3-6-10a6 6 0 0 1 12 0c0 4.7-6 10-6 10z"/><circle cx="12" cy="11" r="2.2"/>',
          calendar: '<rect x="4" y="5" width="16" height="15" rx="2"/><path d="M4 10h16M9 3v4M15 3v4"/>',
          clock: '<circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/>',
          send: '<path d="M21 3L10 14M21 3l-7 18-4-7-7-4z"/>',
          home: '<path d="M4 11l8-7 8 7v9H4z"/>',
        };
        container.replaceChildren(
          ...rows.map(([label, value, icon]) => {
            const row = document.createElement('p');
            row.className = 'ddc-kv';
            if (icon && icons[icon]) {
              const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
              svg.setAttribute('viewBox', '0 0 24 24');
              svg.setAttribute('aria-hidden', 'true');
              svg.setAttribute('class', 'ddc-kv__icon');
              svg.innerHTML = icons[icon];
              row.append(svg);
            }
            if (label) {
              const name = document.createElement('span');
              name.className = 'ddc-kv__label';
              name.textContent = label;
              row.append(name);
            }
            const val = document.createElement('strong');
            val.className = 'ddc-kv__value';
            val.textContent = value;
            row.append(val);
            return row;
          })
        );
      }

      renderReview() {
        const t = this.totals();
        const slot = this.selectedSlot();
        this.reviewRows(
          this.querySelector('[data-review="delivery"]'),
          [
            t.zone ? ['Zona', t.zone.name, 'pin'] : null,
            ['Fecha', this.prettyDate(this.dateInput.value), 'calendar'],
            this.value('[data-ddc-address]')
              ? ['Dirección', [this.value('[data-ddc-address]'), this.value('[data-ddc-address2]')].filter(Boolean).join(' · '), 'home']
              : ['Dirección', 'Pendiente', 'home'],
            [
              'Franja',
              slot
                ? `${slot.dataset.label}${this.isSpecial() ? ` · +${this.money(this.settings.special.price)}` : ''}`
                : 'Franja pendiente',
              'clock',
            ],
          ].filter(Boolean)
        );

        const sender = [this.value('[data-ddc-sender-name]'), this.value('[data-ddc-sender-phone]')].filter(Boolean);
        const recipient = [this.value('[data-ddc-recipient-name]'), this.value('[data-ddc-recipient-phone]')].filter(
          Boolean
        );
        this.reviewRows(this.querySelector('[data-review="contacts"]'), [
          ['Quién envía', [...sender, this.value('[data-ddc-sender-email]')].filter(Boolean).join(' · ') || 'Pendiente', 'send'],
          ['Quién recibe', recipient.join(' · ') || 'Pendiente', 'home'],
        ]);

        const occasion = this.value('[data-ddc-occasion]');
        const colors = this.selectedColors();
        const message = this.value('[data-ddc-message]');
        const signature = this.signatureText();
        this.reviewRows(
          this.querySelector('[data-review="personalization"]'),
          [
            occasion ? ['Ocasión', occasion] : null,
            ['Colores', colors.join(' y ') || 'Sin seleccionar'],
            ['Mensaje', message || 'Sin mensaje'],
            signature ? ['Firma', signature] : null,
          ].filter(Boolean)
        );

        const extras = this.querySelector('[data-review="extras"]');
        if (t.addons.length) {
          extras.replaceChildren(...t.addons.map((addon) => this.lineRow(addon.title, `+${this.money(addon.price)}`)));
        } else {
          this.reviewRows(extras, [['', 'Sin adicionales']]);
        }
      }

      showCartError(message) {
        const error = this.querySelector('[data-ddc-cart-error]');
        error.textContent = message;
        error.hidden = !message;
      }

      /* ---------- Cart (Shopify Ajax Cart API) ---------- */

      buildItems() {
        if (!this.variantReady()) throw new Error(DdcConfigurator.VARIANT_ERROR);
        const t = this.totals();
        if (!this.deliveryFeeReady(t.zone)) throw new Error(DdcConfigurator.DELIVERY_FEE_ERROR);
        const orderId = `DDC-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        this.querySelector('[data-ddc-group-value]').value = orderId;

        const properties = {};
        new FormData(this.form).forEach((value, key) => {
          const match = /^properties\[(.+)\]$/.exec(key);
          const text = String(value).trim();
          if (match && text) properties[match[1]] = text;
        });
        if (this.isAnonymous()) {
          properties['Firma de la tarjeta'] = DdcConfigurator.ANONYMOUS_SIGNATURE;
          properties['Anónimo'] = 'Sí';
        } else {
          delete properties['Anónimo'];
        }

        const linked = { 'Para el pedido': this.settings.productTitle, _ddc_pedido: orderId };
        const items = [{ id: Number(this.variant.id), quantity: 1, properties }];
        items.push({ id: t.zone.variantId, quantity: 1, properties: { ...linked, _ddc_tipo: 'domicilio' } });
        if (this.isSpecial()) {
          items.push({ id: this.settings.special.variantId, quantity: 1, properties: { ...linked, _ddc_tipo: 'horario' } });
        }
        t.addons.forEach((addon) =>
          items.push({ id: addon.variantId, quantity: 1, properties: { ...linked, _ddc_tipo: 'adicional' } })
        );
        // Shopify renders multi-add responses in reverse order; submit the main product last so it appears first.
        return items.reverse();
      }

      // The cart must contain exactly the lines of this order (one domicilio) and its total.
      cartMatchesOrder(cart, items, expectedTotal) {
        if (!cart || !Array.isArray(cart.items)) return false;
        const expectedCount = items.reduce((sum, item) => sum + item.quantity, 0);
        if (Number(cart.item_count) !== expectedCount) return false;
        const subtotal = cart.items.reduce((sum, line) => sum + Number(line.original_line_price ?? line.line_price ?? 0), 0);
        if (subtotal !== Number(expectedTotal)) return false;
        const zoneItem = items.find((item) => item.properties?._ddc_tipo === 'domicilio');
        const zoneId = Number(zoneItem?.id);
        const zoneLines = cart.items.filter((line) => Number(line.variant_id) === zoneId);
        if (zoneLines.length !== 1 || Number(zoneLines[0].quantity) !== 1) return false;
        const expectedIds = items.map((item) => Number(item.id)).sort((a, b) => a - b);
        const cartIds = cart.items
          .flatMap((line) => Array(Number(line.quantity) || 0).fill(Number(line.variant_id)))
          .sort((a, b) => a - b);
        return expectedIds.length === cartIds.length && expectedIds.every((id, i) => id === cartIds[i]);
      }

      buildCartAttributes() {
        const slot = this.selectedSlot();
        if (!this.dateInput.value || !slot) throw new Error('Selecciona una fecha y una franja de entrega válidas.');
        let origen = {};
        try { origen = (window.ddcAttribution && window.ddcAttribution.attrs()) || {}; } catch (e) { origen = {}; }
        return {
          ...origen,
          'Order Due Date': this.dateInput.value,
          'Order Due Time': slot.dataset.label,
          'Order Fulfillment Type': 'Local Delivery',
        };
      }

      // Shopify checkout prefill from data captured in step 2 (only non-empty values are sent).
      checkoutPrefill() {
        const zone = this.selectedZone();
        const { firstName, lastName } = this.splitName(this.value('[data-ddc-recipient-name]'));
        const street = this.value('[data-ddc-address]');
        const notes = this.value('[data-ddc-address2]');
        const showsAddress2 = DdcConfigurator.CHECKOUT_SHOWS_ADDRESS2;
        return {
          'checkout[email]': this.value('[data-ddc-sender-email]'),
          'checkout[shipping_address][first_name]': firstName,
          'checkout[shipping_address][last_name]': lastName,
          'checkout[shipping_address][address1]': showsAddress2 ? street : [street, notes].filter(Boolean).join(' — '),
          'checkout[shipping_address][address2]': showsAddress2 ? notes : '',
          'checkout[shipping_address][city]': zone?.city || '',
          'checkout[shipping_address][province]': zone?.province || '',
          'checkout[shipping_address][country]': 'CO',
          'checkout[shipping_address][phone]': this.value('[data-ddc-recipient-phone]'),
          locale: 'es',
        };
      }

      // First word → first_name, the rest → last_name. A single word remains usable in first_name.
      splitName(raw) {
        const words = String(raw || '').trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) return { firstName: '', lastName: '' };
        if (words.length === 1) return { firstName: words[0], lastName: '-' };
        return { firstName: words[0], lastName: words.slice(1).join(' ') };
      }

      checkoutUrl(root) {
        const params = new URLSearchParams();
        Object.entries(this.checkoutPrefill()).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        return `${root}checkout?${params.toString()}`;
      }

      async checkout({ replaceExisting = false } = {}) {
        if (this.editing || this.submitting) return;

        if (!this.validateVariant()) return;

        const deliveryInvalid = this.validateDelivery();
        if (deliveryInvalid) {
          this.startEdit('delivery');
          this.validateDelivery();
          return;
        }
        if (this.validateColors()) {
          this.startEdit('personalization');
          this.validateColors();
          return;
        }
        const contactsInvalid = this.validateContacts();
        if (contactsInvalid) {
          this.showStep(DdcConfigurator.CONTACTS_STEP);
          this.focusInvalid(contactsInvalid);
          this.announceMissing(contactsInvalid);
          return;
        }

        this.submitting = true;
        this.showCartError('');
        this.render();

        const root = (this.settings.rootUrl || '/').replace(/\/?$/, '/');
        const replaceButton = this.querySelector('[data-ddc-replace-cart]');
        try {
          const currentResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
          const currentCart = await currentResponse.json().catch(() => null);
          if (!currentResponse.ok || !currentCart) throw new Error(DdcConfigurator.PREPARE_ERROR);
          if (Number(currentCart.item_count) > 0 && !replaceExisting) {
            this.submitting = false;
            this.render();
            this.showCartError(DdcConfigurator.CART_EXISTS);
            if (replaceButton) {
              replaceButton.hidden = false;
              replaceButton.focus();
            }
            return;
          }
          if (replaceButton) replaceButton.hidden = true;

          const cartAttributes = this.buildCartAttributes();
          const items = this.buildItems();
          const expectedTotal = this.totals().total;

          if (replaceExisting && Number(currentCart.item_count) > 0) {
            const clearResponse = await fetch(`${root}cart/clear.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            });
            const clearData = await clearResponse.json().catch(() => ({}));
            if (!clearResponse.ok || Number(clearData.item_count) !== 0) {
              throw new Error(DdcConfigurator.PREPARE_ERROR);
            }
          }

          const putAttributes = (attributes) => fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes }),
          });
          let attributesResponse = await putAttributes(cartAttributes);
          if (!attributesResponse.ok) {
            // Si el origen (_ddc_*) fuera rechazado, se reintenta solo con la entrega: el origen nunca bloquea la venta.
            const soloEntrega = {};
            ['Order Due Date', 'Order Due Time', 'Order Fulfillment Type'].forEach((key) => { soloEntrega[key] = cartAttributes[key]; });
            attributesResponse = await putAttributes(soloEntrega);
          }
          const attributesData = await attributesResponse.json().catch(() => ({}));
          // Solo se exige la entrega; los datos de origen (_ddc_*) nunca deben bloquear una compra.
          const attributesSaved = ['Order Due Date', 'Order Due Time', 'Order Fulfillment Type'].every(
            (key) => attributesData.attributes?.[key] === cartAttributes[key]
          );
          if (!attributesResponse.ok || !attributesSaved) {
            throw new Error('No pudimos guardar la fecha y la franja de entrega.');
          }

          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.description || data.message || 'No pudimos agregar tu pedido al carrito.');
          }

          const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
          const cart = await cartResponse.json().catch(() => null);
          if (!cartResponse.ok || !this.cartMatchesOrder(cart, items, expectedTotal)) {
            throw new Error(DdcConfigurator.PREPARE_ERROR);
          }
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'ddc_checkout_submit', {
              currency: 'COP',
              value: Number(expectedTotal) / 100,
              checkout_type: 'configurator',
              items: items.map((item) => ({
                item_variant: String(item.id),
                quantity: Number(item.quantity) || 1,
              })),
            });
          }
          window.location.href = this.checkoutUrl(root);
        } catch (error) {
          this.submitting = false;
          this.render();
          this.showCartError(
            error.message === DdcConfigurator.PREPARE_ERROR
              ? DdcConfigurator.PREPARE_ERROR
              : `${error.message} Revisa tu pedido e inténtalo de nuevo.`
          );
        }
      }
    }
  );
}
